import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { usePublicServiceMenuItems } from '@/hooks/usePublicServiceMenuItems';
import { usePublicCountry } from '@/contexts/PublicCountryContext';

interface PublicServicesMenuProps {
  /** Called after navigation (e.g. close parent mobile sheet). */
  onNavigate?: () => void;
  /** `button` = header trigger; `list` = links only for embedding in another sheet. */
  variant?: 'button' | 'list';
  className?: string;
}

/**
 * Services menu — address search, tourism, permit application (FAQ/Support are separate links).
 * No login required. Used in PublicHeader and SharedHeader (mobile + desktop).
 */
export default function PublicServicesMenu({
  onNavigate,
  variant = 'button',
  className,
}: PublicServicesMenuProps) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const items = usePublicServiceMenuItems();
  const { country } = usePublicCountry();

  const handleNavigate = () => {
    setOpen(false);
    onNavigate?.();
  };

  const linkList = (
    <nav className={cn('flex flex-col gap-1', className)} aria-label="Public services">
      {items.map((item) => {
        const Icon = item.icon;
        const active =
          item.path === '/'
            ? location.pathname === '/'
            : location.pathname === item.path ||
              location.pathname.startsWith(`${item.path}/`);

        return (
          <Link
            key={item.id}
            to={item.path}
            onClick={handleNavigate}
            className={cn(
              'flex items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors',
              active ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
            )}
          >
            <Icon className="h-5 w-5 mt-0.5 shrink-0" aria-hidden />
            <span>
              <span className="block font-medium text-sm">{item.label}</span>
              <span className="block text-xs text-muted-foreground mt-0.5">{item.description}</span>
            </span>
          </Link>
        );
      })}
    </nav>
  );

  if (variant === 'list') {
    return (
      <div className={className}>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground px-3 mb-2">
          Services
        </p>
        {linkList}
      </div>
    );
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('flex items-center gap-2 min-h-[44px]', className)}
          aria-label="Open public services menu"
        >
          <Menu className="h-4 w-4 sm:hidden" aria-hidden />
          <Compass className="h-4 w-4 hidden sm:block" aria-hidden />
          <span className="hidden sm:inline">Services</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[min(100vw,20rem)]">
        <SheetHeader>
          <SheetTitle>Services</SheetTitle>
        </SheetHeader>
        <p className="text-sm text-muted-foreground mt-1 mb-3 px-1">
          {country
            ? `Showing services for ${country.name}. No sign-in required.`
            : 'Find a QLS address works worldwide. Local services appear for the country you are browsing from.'}
        </p>
        {linkList}
      </SheetContent>
    </Sheet>
  );
}
